import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Mail, Calendar, Upload, Download, Trash2, FileText, Check, ChevronDown, ChevronUp, UserCircle } from 'lucide-react';
import api from '../../utils/authUtils';
import { useNavigate, Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';
const BLUE = '#49ABDB';

// Near full-width, like the public site: small side margin on desktop, edge-to-edge on mobile.
const Page = styled.div`margin:1.5rem 1rem 3rem;position:relative;@media(max-width:640px){margin:0 0 2rem;}`;

// White hero band (full width) with centered inner content
const Hero = styled.div`position:relative;background:#fff;box-shadow:0 25px 50px -12px rgba(0,0,0,.18);`;
const HeroInner = styled.div`max-width:78rem;margin:0 auto;padding:3.75rem 2.5rem 3.25rem;@media(max-width:600px){padding:2.5rem 1.5rem;}`;
const BigHeadline = styled.h1`margin:0;color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.02em;line-height:1.04;font-size:clamp(2.2rem,5.2vw,3.8rem);`;
const SubHeadline = styled.p`margin:.85rem 0 0;color:#5B5B5B;font-weight:700;font-size:clamp(1rem,1.9vw,1.35rem);`;
const TagLine = styled.p`margin:.5rem 0 0;color:#5B5B5B;font-weight:400;font-style:italic;font-size:clamp(.85rem,1.3vw,1rem);`;
// Blue-grey content band (full width), single centered column - no identity
// panel here anymore, that lives on the one universal /profile page.
const Grey = styled.div`background:#d0e6ef;`;
const GreyInner = styled.div`max-width:52rem;margin:0 auto;padding:2rem 2.5rem;@media(max-width:640px){padding:1.5rem;}`;

const CrossLink = styled(Link)`
  display:flex;align-items:center;gap:.5rem;background:#fff;color:#0369a1;text-decoration:none;
  padding:.7rem .9rem;margin-bottom:1.5rem;box-shadow:0 6px 16px rgba(15,23,42,.06);font-size:.9rem;font-weight:600;
  &:hover{text-decoration:underline;}
`;

// Conferences / invites
const ConfHeader = styled.div`display:inline-flex;align-items:center;gap:.45rem;color:#111D4E;font-weight:800;font-size:1.05rem;margin:0 0 1rem;padding-bottom:5px;background-image:linear-gradient(to right,#4e4094,#14cbde);background-size:100% 2px;background-position:0 bottom;background-repeat:no-repeat;`;
const InviteCard = styled.div`display:flex;flex-direction:column;background:#fff;padding:.9rem;margin-bottom:.8rem;box-shadow:0 6px 16px rgba(15,23,42,.06);`;
const CardTop = styled.div`display:flex;gap:1rem;align-items:center;`;
const Thumb = styled.img`width:112px;height:82px;object-fit:cover;flex:none;background:#eef2f7;`;
const ThumbFallback = styled.div`width:112px;height:82px;flex:none;background:#eef2f7;display:flex;align-items:center;justify-content:center;color:#94a3b8;`;
const InviteInfo = styled.div`min-width:0;`;
const InviteName = styled.div`font-weight:700;color:#111D4E;font-size:1.02rem;`;
const InviteMeta = styled.div`color:#5B6B78;font-size:.85rem;margin-top:.12rem;`;
const StatusTag = styled.span`display:inline-block;margin-top:.35rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:${p=>p.$confirmed?'#dcfce7':'#e0f2fe'};color:${p=>p.$confirmed?'#166534':'#075985'};`;
const InviteHint = styled.div`display:flex;align-items:center;gap:.3rem;margin-top:.3rem;font-size:.74rem;color:#92400e;`;
const Empty = styled.p`color:#5B6B78;`;

// "Your talk" panel inside a confirmed conference card
const TalkArea = styled.div`margin-top:.7rem;padding-top:.7rem;border-top:1px solid rgba(15,23,42,.08);`;
// Collapsed-by-default disclosure header: keeps the card compact and shows a
// one-line summary of the talk (or an invite to add one) until expanded.
const ToggleRow = styled.button`display:flex;width:100%;justify-content:space-between;align-items:center;gap:.5rem;border:0;background:transparent;padding:0;cursor:pointer;text-align:left;color:#94a3b8;&:hover{color:#5B6B78;}`;
const ToggleLeft = styled.span`display:flex;flex-direction:column;gap:.15rem;min-width:0;`;
const ToggleTitle = styled.span`font-size:.68rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#334155;`;
const ToggleSummary = styled.span`display:flex;gap:.5rem;align-items:center;min-width:0;font-size:.82rem;color:#111D4E;`;
const SummaryTitle = styled.span`overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600;`;
const SummarySlides = styled.span`display:inline-flex;gap:.25rem;align-items:center;flex:none;font-size:.74rem;color:#5B6B78;`;
const TalkBody = styled.div`margin-top:.5rem;`;
const FieldLabel = styled.label`display:block;font-size:.72rem;font-weight:700;color:#5B6B78;margin:.5rem 0 .25rem;`;
const TitleRow = styled.div`display:flex;gap:.4rem;align-items:stretch;`;
const TalkInput = styled.input`flex:1;min-width:0;padding:.5rem .55rem;border:1px solid #cbd5e1;font:inherit;font-size:.9rem;`;
const MiniButton = styled.button`display:inline-flex;align-items:center;gap:.3rem;border:0;padding:.5rem .7rem;background:${p=>p.$ghost?'#eef2f7':BLUE};color:${p=>p.$ghost?'#334155':'#fff'};font-weight:700;font-size:.82rem;cursor:pointer;transition:background-color .3s;&:hover:not(:disabled){background:${p=>p.$ghost?'#e2e8f0':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const SlideBtnRow = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;align-items:center;`;
const SlideFile = styled.div`display:flex;gap:.4rem;align-items:center;min-width:0;background:#f8fafc;border:1px solid #e2e8f0;padding:.35rem .55rem;font-size:.85rem;color:#111D4E;`;
const SlideName = styled.a`min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#0369a1;text-decoration:none;&:hover{text-decoration:underline;}`;
const IconButton = styled.button`display:inline-flex;align-items:center;border:0;background:transparent;color:${p=>p.$danger?'#dc2626':'#0369a1'};cursor:pointer;padding:.15rem;&:hover:not(:disabled){color:${p=>p.$danger?'#b91c1c':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const TalkHint = styled.div`font-size:.7rem;color:#94a3b8;margin-top:.4rem;`;
const TalkError = styled.div`font-size:.78rem;color:#b91c1c;margin-top:.4rem;`;

// Per-engagement talk editor: title (explicit save) + slides upload/download/remove.
// Every action returns the refreshed profile, which the parent swaps in.
const TalkPanel = ({ engagement, onUpdated }) => {
  const intl = useIntl();
  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(engagement.talkTitleSuggestion || engagement.talkTitle || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const currentTitle = engagement.talkTitleSuggestion || engagement.talkTitle || '';
  const changed = title.trim() !== currentTitle;
  const adminOwned = Boolean(engagement.talkTitle);

  const saveTitle = async () => {
    setSaving(true); setError('');
    try {
      const { data } = await api.put(`/api/speaker/engagements/${engagement.eventId}/talk`, { title });
      onUpdated(data); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { setError(err.response?.data || t('speaker.talk.saveError', 'Could not save the talk title.')); }
    finally { setSaving(false); }
  };
  const pickFile = () => fileRef.current?.click();
  const onFile = async e => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setUploading(true); setError('');
    try {
      const form = new FormData(); form.append('file', file);
      const { data } = await api.post(`/api/speaker/engagements/${engagement.eventId}/slides`, form);
      onUpdated(data);
    } catch (err) { setError(err.response?.data || t('speaker.talk.uploadError', 'Upload failed. Please try again.')); }
    finally { setUploading(false); }
  };
  const removeSlides = async () => {
    setUploading(true); setError('');
    try {
      const { data } = await api.delete(`/api/speaker/engagements/${engagement.eventId}/slides`);
      onUpdated(data);
    } catch (err) { setError(err.response?.data || t('speaker.talk.removeError', 'Could not remove the file.')); }
    finally { setUploading(false); }
  };

  const needsSlides = engagement.requiresPresentation !== false;
  const hasContent = Boolean(engagement.talkTitle || engagement.slidesUrl);

  return (
    <TalkArea>
      <ToggleRow type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <ToggleLeft>
          <ToggleTitle>
            {hasContent
              ? <FormattedMessage id="speaker.talk.header" defaultMessage="Your talk" />
              : needsSlides
                ? <FormattedMessage id="speaker.talk.add" defaultMessage="Add your talk & slides" />
                : <FormattedMessage id="speaker.talk.addTitleOnly" defaultMessage="Add your talk title" />}
          </ToggleTitle>
          {!open && hasContent && (
            <ToggleSummary>
              {engagement.talkTitle && <SummaryTitle>{engagement.talkTitle}</SummaryTitle>}
              {engagement.slidesUrl && <SummarySlides><FileText size={12} />{engagement.slidesFilename}</SummarySlides>}
            </ToggleSummary>
          )}
        </ToggleLeft>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </ToggleRow>
      {open && (
      <TalkBody>
      <FieldLabel>{adminOwned
        ? <FormattedMessage id="speaker.talk.titleAdminLabel" defaultMessage="Talk title (set by organizer)" />
        : <FormattedMessage id="speaker.talk.titleLabel" defaultMessage="Talk title" />}</FieldLabel>
      <TitleRow>
        <TalkInput value={title} onChange={e => setTitle(e.target.value)} placeholder={t('speaker.talk.titlePlaceholder', 'e.g. The Future of Valve Repair')} />
        <MiniButton onClick={saveTitle} disabled={saving || !changed}>
          {saved ? <><Check size={14} /><FormattedMessage id="speaker.talk.saved" defaultMessage="Saved" /></>
                 : adminOwned
                   ? (engagement.talkTitleSuggestion
                       ? <FormattedMessage id="speaker.talk.suggestPending" defaultMessage="Change requested" />
                       : <FormattedMessage id="speaker.talk.suggest" defaultMessage="Suggest change" />)
                   : <FormattedMessage id="speaker.talk.save" defaultMessage="Save" />}
        </MiniButton>
      </TitleRow>
      {adminOwned && (
        <TalkHint>
          {engagement.talkTitleSuggestion
            ? <FormattedMessage id="speaker.talk.pending" defaultMessage="Title change request pending review" />
            : <FormattedMessage id="speaker.talk.suggestionHint" defaultMessage="The organizer will review your suggestion." />}
        </TalkHint>
      )}

      <FieldLabel><FormattedMessage id="speaker.talk.slidesLabel" defaultMessage="Slides" /></FieldLabel>
      {needsSlides || engagement.slidesUrl ? (
        <>
          <input ref={fileRef} type="file" accept=".pptx,.ppt,.pdf,.key,.odp" style={{ display: 'none' }} onChange={onFile} />
          {engagement.slidesUrl ? (
            <SlideBtnRow>
              <SlideFile>
                <FileText size={15} color="#5B6B78" />
                <SlideName href={`${API}${engagement.slidesUrl}`} target="_blank" rel="noreferrer" download={engagement.slidesFilename}>
                  {engagement.slidesFilename || t('speaker.talk.slidesLabel', 'Slides')}
                </SlideName>
                <IconButton as="a" href={`${API}${engagement.slidesUrl}`} target="_blank" rel="noreferrer" download={engagement.slidesFilename} title={t('speaker.talk.download', 'Download')}><Download size={15} /></IconButton>
                <IconButton $danger disabled={uploading} onClick={removeSlides} title={t('speaker.talk.remove', 'Remove')}><Trash2 size={15} /></IconButton>
              </SlideFile>
              <MiniButton $ghost disabled={uploading} onClick={pickFile}>
                <Upload size={14} />{uploading ? t('speaker.talk.uploading', 'Uploading…') : t('speaker.talk.replace', 'Replace')}
              </MiniButton>
            </SlideBtnRow>
          ) : (
            <MiniButton $ghost disabled={uploading} onClick={pickFile}>
              <Upload size={14} />{uploading ? t('speaker.talk.uploading', 'Uploading…') : t('speaker.talk.upload', 'Upload slides')}
            </MiniButton>
          )}
          <TalkHint><FormattedMessage id="speaker.talk.formats" defaultMessage="PowerPoint, Keynote, PDF or ODP · up to 100 MB" /></TalkHint>
        </>
      ) : (
        <TalkHint><FormattedMessage id="speaker.talk.slidesNotRequired" defaultMessage="No presentation is required for this engagement." /></TalkHint>
      )}
      {error && <TalkError>{error}</TalkError>}
      </TalkBody>
      )}
    </TalkArea>
  );
};

// Event thumbnail with graceful fallback (missing URL or image that fails to load).
const EventThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <ThumbFallback><Calendar size={22} /></ThumbFallback>;
  return <Thumb src={`${API}${url}`} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
};

// The Speaker Hub is purely about speaking engagements (talk title, slides)
// now - there's no speaker-specific identity/profile here anymore. Name,
// bio, specialty, photo, interests etc. all live on the one universal
// /profile page, same as for every other role (docs/profile-unification-design.md).
const SpeakerDashboard = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get('/api/speaker/profile')
      .then(({ data }) => setProfile(data))
      .catch(err => { if (err.response?.status === 401) navigate('/speaker/login'); else setProfile({ error: true }); });
  }, [navigate]);

  const statusLabel = s => t(`speaker.engagement.${s}`, s);
  const formatDate = v => v ? new Date(v).toLocaleString(intl.locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  if (!profile) return <Page><Hero><Empty>{t('speaker.dashboard.loading', 'Loading your engagements…')}</Empty></Hero></Page>;
  if (profile.error) return <Page><Hero><Empty>{t('speaker.dashboard.loadError', 'Your speaking engagements could not be loaded.')}</Empty></Hero></Page>;

  const engagements = profile.engagements || [];

  return (
    <Page>
      <Hero>
        <HeroInner>
          <BigHeadline>YOUR MEDICAL CARE PILOT</BigHeadline>
          <SubHeadline>Build your Best Care with Heartport</SubHeadline>
          <TagLine>Here to support your medical superpowers with research insights and AI-enhanced clinical care tools.</TagLine>
        </HeroInner>
      </Hero>

      <Grey>
        <GreyInner>
          <CrossLink to="/profile">
            <UserCircle size={16} />
            {t('speaker.crossLink.profile', 'View your full profile & history')}
          </CrossLink>

          <ConfHeader><Calendar size={17} />HEARTS Conferences</ConfHeader>
          {engagements.length === 0
            ? <Empty><FormattedMessage id="speaker.dashboard.none" defaultMessage="You have no upcoming speaking engagements right now." /></Empty>
            : engagements.map(item => (
              <InviteCard key={item.eventId}>
                <CardTop>
                  <EventThumb url={item.eventImageUrl} />
                  <InviteInfo>
                    <InviteName>{item.eventName}</InviteName>
                    <InviteMeta>{[formatDate(item.eventDateTime), item.eventLocation].filter(Boolean).join(' · ')}</InviteMeta>
                    <StatusTag $confirmed={item.status === 'CONFIRMED'}>{statusLabel(item.status)}</StatusTag>
                    {item.status === 'INVITED' && (
                      <InviteHint title={t('speaker.dashboard.pendingHint', 'Check your email to accept this invitation.')}>
                        <Mail size={12} /><FormattedMessage id="speaker.dashboard.pendingHint" defaultMessage="Check your email to accept this invitation." />
                      </InviteHint>
                    )}
                  </InviteInfo>
                </CardTop>
                {item.status === 'CONFIRMED' && <TalkPanel engagement={item} onUpdated={setProfile} />}
              </InviteCard>
            ))}
        </GreyInner>
      </Grey>
    </Page>
  );
};

export default SpeakerDashboard;
